import { getTranslate } from 'legacy-src/utilities/i18n'
import Image from 'next/image'
import React from 'react'

import { type StrapiArticleType } from '@/legacy-src/components/hedepyPlus/types'
import { type StrapiObject } from '@/legacy-src/lib/strapi/types'
import { cn } from '@/legacy-src/lib/utils'

import LinkWithQueryForwarding from '../button/LinkWithQueryForwarding'
import GliderClient from '../ClientGlider'
import Icon from '../icons/Icon'
import { chooseCorrectColors } from './utils/chooseCorrectColors'

type CounsellingArticleProps = {
  readonly contentData: StrapiArticleType
  readonly isLocked?: boolean
  readonly isPreview: boolean
  readonly isPublic?: boolean
  readonly className?: string
  readonly baseLink?: string
  readonly baseLinkIndividual?: boolean
}

const ArticlePreview: React.FC<CounsellingArticleProps> = ({
  contentData,
  isLocked,
  isPreview,
  isPublic,
  className,
  baseLink = 'articles/hedepy',
  baseLinkIndividual,
}) => {
  const { t } = getTranslate()
  const headerBgColor = 'bg-blue-500'
  const slug = contentData.slug

  if (baseLinkIndividual)
    baseLink = contentData?.category === 'article' ? '/articles/blog' : `/articles/${contentData?.category}`

  const imgFromStrapi = contentData?.cover?.data?.attributes?.url
  const isSvg = imgFromStrapi?.includes('.svg')
  const imgSrc = imgFromStrapi && isSvg ? chooseCorrectColors(imgFromStrapi) : imgFromStrapi
  const imageLayout = contentData.coverLayout ?? 'center'
  const imgAlt =
    contentData.cover?.data?.attributes?.alternativeText ?? contentData.title ?? t('general.illustrationImage')

  const article = (
    <div className="relative grow pb-8 tablet:pb-12">
      {isLocked && (
        <div className="absolute right-6 top-6 z-10 flex shrink rounded-full bg-petrol-500 bg-opacity-100">
          <Icon icon="lock-alt" color="white" size="md" className="m-6" />
        </div>
      )}
      <div className={`p-3 ${isLocked && 'opacity-40'}`}>
        <div className={`${headerBgColor} relative mb-6 h-[200px] w-full overflow-hidden rounded-3xl text-center`}>
          {imgSrc &&
            (isSvg ? (
              <img src={imgSrc} loading="lazy" alt={imgAlt} className="mx-auto h-full p-6" />
            ) : (
              <Image
                alt={imgAlt}
                src={imgSrc}
                className={cn(
                  'h-[200px] w-full rounded-3xl object-contain p-6',
                  imageLayout === 'fill' && 'object-cover p-0',
                )}
                fill
                sizes="137px"
              />
            ))}
        </div>
        <h2 className="mb-3 text-xl text-text-color">{contentData.title}</h2>
        <div
          className={`text-sm leading-6 text-text-color ${
            isPublic ? 'line-clamp-2' : 'line-clamp-4'
          } hover:no-underline`}
        >
          {contentData.perex}
        </div>
        <div className="absolute bottom-0 right-0 flex">
          <div className="mt-2 flex shrink items-center text-right tablet:mt-4">
            <div className="mr-3 text-base font-bold text-text-color">
              {t('component.premiumContent.lessonFooter.showFull')}
            </div>
            <Icon icon="arrow" size="md" />
          </div>
        </div>
      </div>
    </div>
  )

  if (!isPreview && isLocked) return <>{article}</>

  return (
    <LinkWithQueryForwarding
      href={isLocked ? `${baseLink}` : `${baseLink}/${slug}`}
      className={`${className} flex self-stretch hover:no-underline`}
      key={contentData.slug}
    >
      {article}
    </LinkWithQueryForwarding>
  )
}

type Props = {
  readonly data: StrapiObject<StrapiArticleType>[]
  readonly isPreview: boolean
  readonly isPublic?: boolean
  readonly baseLink: string
  readonly baseLinkIndividual?: boolean
}

const ArticlesList: React.FC<Props> = ({ data, isPreview, isPublic, baseLink, baseLinkIndividual }) => {
  const articles = data

  const renderGlider = () => (
    <GliderClient className="articles" draggable slidesToShow={1.5} slidesToScroll={1}>
      {articles?.map((item) => {
        const contentData = item.attributes

        return (
          <ArticlePreview
            key={contentData.slug}
            contentData={contentData}
            isPreview={isPreview}
            isPublic={isPublic}
            baseLink={baseLink}
            baseLinkIndividual={baseLinkIndividual}
            className="mb-6 mr-4 h-full basis-[300px]"
          />
        )
      })}
    </GliderClient>
  )

  const renderRegularList = () => (
    <div className="grid w-full grid-cols-1 gap-6 tablet:grid-cols-2 desktop:grid-cols-3">
      {articles?.map((item) => {
        const contentData = item.attributes
        return (
          <ArticlePreview
            key={contentData.slug}
            contentData={contentData}
            isPreview={isPreview}
            isPublic={isPublic}
            baseLink={baseLink}
            baseLinkIndividual={baseLinkIndividual}
          />
        )
      })}
    </div>
  )

  if (!articles) return <p>There is no content for this version of language.</p>

  if (isPreview)
    return (
      <>
        <div className="tablet:hidden">{renderGlider()}</div>
        <div className="hidden tablet:block">{renderRegularList()}</div>
      </>
    )

  return renderRegularList()
}

export default ArticlesList
